/**
 * Hrefs can be simple hash links that direct to page anchors, e.g. (#top-of-page). So we need to test for that.
 *
 * Checks for validity as per the URL fragment definition in https://www.rfc-editor.org/rfc/rfc3986#section-3.5
 */
function isValidUrlFragment(hash: string): boolean {
  return /^#(?:[a-zA-Z0-9:@\-.~_?!$&'()*+,;=]|%[A-Fa-f0-9]{2})+$/.test(hash);
}

// From DOMPurify
// https://github.com/cure53/DOMPurify/blob/main/src/regexp.js
const ATTR_WHITESPACE = /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g; // eslint-disable-line no-control-regex
const IS_ALLOWED_URI = /^(https?:\/\/|mailto:|tel:)/i;

/**
 * Validate URI making sure no whitespace is used
 */
function isAllowedUri(uri: string | undefined) {
  return !uri || uri.replace(ATTR_WHITESPACE, '').match(IS_ALLOWED_URI);
}

/**
 * Add https if there is no protocol like part to the link
 */
export function addMissingProtocol(href: string): string {
  if (href.startsWith('#')) {
    return href;
  }

  const protocolRegex = /^(https?:\/\/|mailto:|tel:)/;

  if (href.replace(ATTR_WHITESPACE, '').startsWith('javascript:')) {
    return '';
  }

  if (!protocolRegex.test(href)) {
    return `https://${href}`;
  }
  return href;
}

/**
 * Ensures a user-supplied href attribute conforms to our expectations. This includes disallowing the usage
 * of the `javascript:` schema.
 */
export function validateHref(href: string): boolean {
  // We should allow `#hash` links to support our anchor plugin
  if (isValidUrlFragment(href)) {
    return true;
  }

  // We don't want to allow arbitrary javascript in these href tags.
  if (!isAllowedUri(href)) {
    return false;
  }

  // We want internet addresses
  const hasTld = new RegExp('((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,})');

  if (!hasTld.test(href)) {
    return false;
  }

  try {
    // Let javascript tell us if it's a URL instead of trying to do regex stuff.
    // This allows us to support "mailto:", "tel:", whatever.
    new URL(href);
  } catch {
    return false;
  }
  return true;
}
